/**
 * Admin — Security/Privileged Access (Wave 1b W1b-11)
 *
 * Three sections wired to /api/v1/admin/security/privileged/*:
 *   1. Break-glass accounts — sealed root-equivalent admin accounts with
 *                              n-of-m unseal + 15-min auto-reseal
 *   2. Admin roles          — list 5 built-ins (read-only) + tenant-defined
 *                              custom roles with hand-picked permissions
 *   3. Impersonation audit  — read-only history + force-end action
 *
 * Mock data has been removed; first paint pulls from the backend.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Lock, Unlock, Shield, Clock, AlertTriangle, CheckCircle2,
  Plus, Trash2, KeySquare, UserCheck, Loader2, MoreHorizontal,
  ThumbsUp, ThumbsDown, RotateCcw,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { toast } from 'sonner';
import { SectionCard } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/security/privileged')({
  component: PrivilegedPage,
});

// ─── Backend types ────────────────────────────────────────────────────────

type AccountStatus = 'sealed' | 'unsealed' | 'rotating';
type UnsealRequestStatus =
  | 'pending'
  | 'approved'
  | 'denied'
  | 'expired'
  | 'executed';
type ImpersonationStatus = 'active' | 'ended' | 'expired' | 'revoked';

interface BreakGlassAccount {
  id: string;
  name: string;
  description: string | null;
  requiredApprovers: number;
  status: AccountStatus;
  unsealedAt: string | null;
  resealAt: string | null;
  unsealedBy: string | null;
  lastRotatedAt: string | null;
  createdAt: string;
  pendingRequestCount: number;
}

interface AdminRole {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isBuiltIn: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

interface ImpersonationSessionRow {
  id: string;
  impersonatorId: string;
  impersonatorEmail: string | null;
  impersonatedUserId: string;
  impersonatedEmail: string | null;
  reason: string;
  status: string;
  startedAt: string;
  endedAt: string | null;
  expiresAt: string;
  ipAddress: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
//  Page
// ═══════════════════════════════════════════════════════════════════════════

function PrivilegedPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Privileged Access</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Break-glass accounts, admin roles, and impersonation audit.
        </p>
      </div>

      <BreakGlassSection />
      <AdminRolesSection />
      <ImpersonationSection />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Break-glass Accounts
// ═══════════════════════════════════════════════════════════════════════════

function BreakGlassSection() {
  const [accounts, setAccounts] = useState<BreakGlassAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [requestingUnseal, setRequestingUnseal] =
    useState<BreakGlassAccount | null>(null);
  const [deleting, setDeleting] = useState<BreakGlassAccount | null>(null);

  // useRef + useEffect cleanup pattern per spec — keeps the auto-refresh
  // timer alive while the page is mounted, cleans up on unmount so we
  // don't leak a setTimeout closure.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiClient.get<{ accounts: BreakGlassAccount[] }>(
        '/admin/security/privileged/accounts'
      );
      setAccounts(data.accounts);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load accounts';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Refresh every 30s while the page is open so the unseal countdown
    // timer + status badges stay current. Cleanup on unmount.
    function tick() {
      refresh();
      refreshTimer.current = setTimeout(tick, 30_000);
    }
    refreshTimer.current = setTimeout(tick, 30_000);
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [refresh]);

  async function deleteAccount(account: BreakGlassAccount) {
    try {
      await apiClient.delete(
        `/admin/security/privileged/accounts/${account.id}`,
        {
          body: JSON.stringify({ confirmed: true }),
          headers: { 'content-type': 'application/json' },
        }
      );
      toast.success(`Break-glass account "${account.name}" deleted`);
      setDeleting(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  }

  async function manualReseal(id: string) {
    try {
      await apiClient.post(
        `/admin/security/privileged/accounts/${id}/reseal`,
        {}
      );
      toast.success('Account resealed');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Reseal failed';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="Break-glass Accounts"
      icon={KeySquare}
      description="Sealed emergency accounts for recovery during IdP outages or catastrophic failure. Unseal requires n-of-m approval; account auto-reseals 15 minutes after unsealing."
      actions={
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1.5"
          onClick={() => setCreating(true)}
          title="Create a new break-glass account"
        >
          <Plus className="h-3 w-3" />
          New break-glass
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading accounts…
        </div>
      ) : accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed rounded-md">
          <KeySquare className="h-6 w-6 text-muted-foreground/50 mb-2" />
          <div className="text-xs font-medium">No break-glass accounts</div>
          <p className="text-[10px] text-muted-foreground mt-1 max-w-sm">
            At least one break-glass account is strongly recommended for
            recovery during IdP outages.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {accounts.map((a) => (
            <BreakGlassRow
              key={a.id}
              account={a}
              onRequestUnseal={() => setRequestingUnseal(a)}
              onReseal={() => manualReseal(a.id)}
              onDelete={() => setDeleting(a)}
              onAfterVote={refresh}
            />
          ))}
        </div>
      )}

      {creating && (
        <CreateBreakGlassDialog
          open
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refresh();
          }}
          existingNames={new Set(accounts.map((a) => a.name))}
        />
      )}

      {requestingUnseal && (
        <UnsealRequestDialog
          open
          account={requestingUnseal}
          onClose={() => setRequestingUnseal(null)}
          onSubmitted={async () => {
            setRequestingUnseal(null);
            await refresh();
          }}
        />
      )}

      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Delete break-glass account"
          description={`Permanently delete "${deleting.name}". All pending unseal requests are also cancelled. This action cannot be undone.`}
          consequences={[
            'Removes the account row + cascades pending unseal requests',
            'Audit chain entry recorded with high-risk action tag',
            'Cannot be undone — credentials are not recoverable',
          ]}
          confirmPhrase={`delete ${deleting.name}`}
          confirmLabel="Delete account"
          variant="danger"
          onConfirm={() => deleteAccount(deleting)}
        />
      )}
    </SectionCard>
  );
}

function BreakGlassRow({
  account,
  onRequestUnseal,
  onReseal,
  onDelete,
  onAfterVote,
}: {
  account: BreakGlassAccount;
  onRequestUnseal: () => void;
  onReseal: () => void;
  onDelete: () => void;
  onAfterVote: () => void;
}) {
  const [pendingOpen, setPendingOpen] = useState(false);

  const isUnsealed = account.status === 'unsealed';
  const resealCountdown =
    account.resealAt && isUnsealed
      ? formatCountdown(new Date(account.resealAt))
      : null;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={cn(
                'h-8 w-8 rounded-md flex items-center justify-center shrink-0',
                isUnsealed
                  ? 'bg-amber-500/10 text-amber-600'
                  : 'bg-emerald-500/10 text-emerald-600'
              )}
            >
              {isUnsealed ? (
                <Unlock className="h-4 w-4" />
              ) : (
                <Lock className="h-4 w-4" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-medium truncate">
                  {account.name}
                </span>
                <StatusBadge status={account.status} />
                {account.pendingRequestCount > 0 && (
                  <Badge
                    variant="outline"
                    className="text-[10px] h-5 px-1.5 gap-1 border-amber-500/40 text-amber-600"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    {account.pendingRequestCount} pending
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                <span>
                  Approvers required: {account.requiredApprovers}-of-M
                </span>
                {resealCountdown && (
                  <span className="text-amber-600 font-medium">
                    <Clock className="h-3 w-3 inline mr-0.5" />
                    Reseals in {resealCountdown}
                  </span>
                )}
                {account.description && (
                  <span className="truncate">{account.description}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {account.pendingRequestCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={() => setPendingOpen(true)}
                title="View pending unseal requests"
              >
                <Shield className="h-3 w-3" />
                Review
              </Button>
            )}
            {!isUnsealed && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={onRequestUnseal}
                title="Request to unseal this account"
              >
                <Unlock className="h-3 w-3" />
                Request unseal
              </Button>
            )}
            {isUnsealed && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                onClick={onReseal}
                title="Manually reseal now (override 15-minute timer)"
              >
                <Lock className="h-3 w-3" />
                Reseal now
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  title="More actions"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="text-xs">
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete account…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>

      {pendingOpen && (
        <PendingRequestsDialog
          open
          accountId={account.id}
          accountName={account.name}
          onClose={() => setPendingOpen(false)}
          onAfterVote={async () => {
            setPendingOpen(false);
            await onAfterVote();
          }}
        />
      )}
    </Card>
  );
}

function StatusBadge({ status }: { status: AccountStatus }) {
  if (status === 'unsealed') {
    return (
      <Badge className="text-[10px] h-5 px-1.5 bg-amber-500 text-white">
        Unsealed
      </Badge>
    );
  }
  if (status === 'rotating') {
    return (
      <Badge className="text-[10px] h-5 px-1.5 bg-blue-500 text-white">
        Rotating
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] h-5 px-1.5">
      <CheckCircle2 className="h-3 w-3 mr-0.5" />
      Sealed
    </Badge>
  );
}

function formatCountdown(target: Date): string {
  const ms = target.getTime() - Date.now();
  if (ms <= 0) return '0s';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m${s.toString().padStart(2, '0')}s`;
}

function CreateBreakGlassDialog({
  open,
  onClose,
  onCreated,
  existingNames,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  existingNames: Set<string>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [requiredApprovers, setRequiredApprovers] = useState(2);
  const [submitting, setSubmitting] = useState(false);

  const nameValid = /^[a-z][a-z0-9-]{2,30}$/.test(name);
  const taken = existingNames.has(name.trim());
  const canSubmit =
    nameValid && !taken && description.trim().length >= 10 && !submitting;

  async function submit() {
    if (!canSubmit) {
      if (!nameValid)
        toast.error(
          'Name must be 3-31 lowercase chars (letters, digits, hyphens), start with a letter'
        );
      else if (taken) toast.error('Name already exists');
      else if (description.trim().length < 10)
        toast.error('Description must be at least 10 characters');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post('/admin/security/privileged/accounts', {
        name: name.trim(),
        description: description.trim(),
        requiredApprovers,
      });
      toast.success(`Break-glass account "${name}" created (sealed)`);
      onCreated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      toast.error(msg);
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <KeySquare className="h-4 w-4" />
            New break-glass account
          </DialogTitle>
          <DialogDescription className="text-xs">
            New break-glass accounts are sealed by default. Unsealing
            requires n-of-m admin approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. emergency-root-2"
              className={cn(
                'h-8 text-xs mt-1 font-mono',
                name.length > 0 && !nameValid && 'border-red-500',
                taken && 'border-red-500'
              )}
              autoComplete="off"
              spellCheck={false}
            />
            {name.length > 0 && !nameValid && (
              <p className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                3-31 lowercase characters (letters, digits, hyphens), starting
                with a letter.
              </p>
            )}
            {taken && (
              <p className="text-[10px] text-red-600 dark:text-red-400 mt-1">
                Name already exists.
              </p>
            )}
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Description
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When should this account be used?"
              className="mt-1 text-[11px] min-h-[60px]"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Minimum 10 characters. Shown to anyone reviewing unseal
              requests.
            </p>
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Required approvers
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                value={requiredApprovers}
                onChange={(e) =>
                  setRequiredApprovers(
                    Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 2))
                  )
                }
                min={1}
                max={5}
                className="h-8 w-24 text-xs tabular-nums"
              />
              <span className="text-[10px] text-muted-foreground">
                of M admins (1-5)
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
            ) : null}
            Create account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnsealRequestDialog({
  open,
  account,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  account: BreakGlassAccount;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = reason.trim().length >= 10 && !submitting;

  async function submit() {
    if (!canSubmit) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post(
        `/admin/security/privileged/accounts/${account.id}/unseal-request`,
        { reason: reason.trim() }
      );
      toast.success(
        `Unseal request submitted. Awaiting ${account.requiredApprovers} approver(s).`
      );
      onSubmitted();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      toast.error(msg);
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Unlock className="h-4 w-4 text-amber-600" />
            Request unseal — {account.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Submit a request to unseal this break-glass account.
            {' '}
            {account.requiredApprovers} other admin(s) must approve before
            the account is unsealed for 15 minutes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Reason for unsealing
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the emergency that requires break-glass access…"
              className="mt-1 text-[11px] min-h-[80px]"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Minimum 10 characters. Logged to the audit chain and shown
              to every approver.
            </p>
          </div>

          <div className="text-[10px] text-muted-foreground p-2 border border-amber-500/40 bg-amber-500/5 rounded">
            <strong>Separation of duties:</strong> as the requestor, you
            cannot vote on this request. Other admins must approve.
            Request expires in 1 hour if not approved.
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
            ) : null}
            Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Pending requests dialog (review + approve/deny). Backed by a separate
// endpoint would be ideal; for v1 we list approve/deny via the existing
// vote endpoints by pulling pending requests via the same refresh path.
// Since we don't have a dedicated GET /requests endpoint in v1, this
// dialog surfaces a simple call-to-action linking to the account.
function PendingRequestsDialog({
  open,
  accountId,
  accountName,
  onClose,
  onAfterVote,
}: {
  open: boolean;
  accountId: string;
  accountName: string;
  onClose: () => void;
  onAfterVote: () => void;
}) {
  const [requestId, setRequestId] = useState('');
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null);

  async function vote(verdict: 'approve' | 'deny') {
    if (!requestId.trim()) {
      toast.error('Enter a request id to vote on');
      return;
    }
    try {
      setSubmitting(verdict);
      await apiClient.post(
        `/admin/security/privileged/accounts/${accountId}/${verdict}/${requestId.trim()}`,
        {}
      );
      toast.success(`Vote ${verdict === 'approve' ? 'approved' : 'denied'}`);
      onAfterVote();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Vote failed';
      toast.error(msg);
      setSubmitting(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-amber-600" />
            Review pending requests — {accountName}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Approve or deny a pending unseal request. Enter the request ID
            from the audit chain or notification email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Request ID
            </Label>
            <Input
              value={requestId}
              onChange={(e) => setRequestId(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="h-8 text-xs mt-1 font-mono"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="text-[10px] text-muted-foreground">
            You cannot vote on a request you submitted (separation of duties).
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => vote('deny')}
            disabled={submitting !== null}
          >
            {submitting === 'deny' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ThumbsDown className="h-3 w-3" />
            )}
            Deny
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => vote('approve')}
            disabled={submitting !== null}
          >
            {submitting === 'approve' ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ThumbsUp className="h-3 w-3" />
            )}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Admin Roles
// ═══════════════════════════════════════════════════════════════════════════

function AdminRolesSection() {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminRole | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ roles: AdminRole[] }>(
        '/admin/security/privileged/admin-roles'
      );
      setRoles(data.roles);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load roles';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function deleteRole(role: AdminRole) {
    if (role.isBuiltIn) {
      toast.error('Built-in roles cannot be deleted');
      return;
    }
    try {
      await apiClient.delete(
        `/admin/security/privileged/admin-roles/${role.id}`
      );
      toast.success(`Role "${role.name}" deleted`);
      setDeleting(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed';
      toast.error(msg);
    }
  }

  // All known permissions (union of all role grants) — used to populate
  // the new-role dialog's permission picker.
  const allPermissions = useMemo(() => {
    const set = new Set<string>();
    for (const r of roles) for (const p of r.permissions) set.add(p);
    return Array.from(set).sort();
  }, [roles]);

  return (
    <SectionCard
      title="Admin Roles"
      icon={Shield}
      description="The shipped built-in roles plus tenant-defined custom roles. Built-ins are read-only; custom roles compose any subset of catalogue permissions."
      actions={
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1.5"
          onClick={() => setCreating(true)}
          title="Create a new custom admin role"
        >
          <Plus className="h-3 w-3" />
          New role
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading roles…
        </div>
      ) : (
        <div className="space-y-2">
          {roles.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-medium">
                        {r.name}
                      </span>
                      {r.isBuiltIn ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-5 px-1.5"
                        >
                          Built-in
                        </Badge>
                      ) : (
                        <Badge
                          className="text-[10px] h-5 px-1.5 bg-blue-500 text-white"
                        >
                          Custom
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className="text-[10px] h-5 px-1.5"
                      >
                        {r.permissions.length} permissions
                      </Badge>
                    </div>
                    {r.description && (
                      <div className="text-[10px] text-muted-foreground mt-1 truncate">
                        {r.description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!r.isBuiltIn && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-red-600"
                        title="Delete custom role"
                        onClick={() => setDeleting(r)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <CreateRoleDialog
          open
          allPermissions={allPermissions}
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}

      {deleting && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setDeleting(null)}
          title="Delete custom role"
          description={`Permanently delete "${deleting.name}". Users currently assigned this role will lose its permission grants.`}
          consequences={[
            'Users with this role lose all permissions granted by it',
            'Audit chain entry recorded',
            'Cannot be undone — recreate from scratch',
          ]}
          confirmPhrase={`delete ${deleting.name}`}
          confirmLabel="Delete role"
          variant="danger"
          onConfirm={() => deleteRole(deleting)}
        />
      )}
    </SectionCard>
  );
}

function CreateRoleDialog({
  open,
  allPermissions,
  onClose,
  onCreated,
}: {
  open: boolean;
  allPermissions: string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const nameValid = /^[a-z][a-z0-9_-]{2,79}$/.test(name);
  const canSubmit = nameValid && selected.size > 0 && !submitting;

  function togglePermission(p: string) {
    const next = new Set(selected);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setSelected(next);
  }

  async function submit() {
    if (!canSubmit) {
      if (!nameValid) toast.error('Name must be lowercase 3-80 chars');
      else if (selected.size === 0) toast.error('Pick at least one permission');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post('/admin/security/privileged/admin-roles', {
        name: name.trim(),
        description: description.trim() || undefined,
        permissions: Array.from(selected),
      });
      toast.success(`Role "${name}" created`);
      onCreated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Create failed';
      toast.error(msg);
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            New custom admin role
          </DialogTitle>
          <DialogDescription className="text-xs">
            Compose a role from the RbacPermission catalogue. Names cannot
            collide with built-in roles.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Name
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. compliance-readonly"
                className="h-8 text-xs mt-1 font-mono"
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Selected
              </Label>
              <div className="h-8 flex items-center text-xs mt-1 tabular-nums">
                {selected.size} permission{selected.size === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Description
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this role is for"
              className="mt-1 text-[11px] min-h-[40px]"
            />
          </div>

          <Separator />

          <div>
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">
              Permissions
            </Label>
            <div className="mt-1 max-h-[260px] overflow-y-auto border rounded-md p-2 space-y-1">
              {allPermissions.length === 0 ? (
                <div className="text-[10px] text-muted-foreground italic">
                  No permissions visible. (The catalogue is loaded with the
                  built-in role list.)
                </div>
              ) : (
                allPermissions.map((p) => (
                  <label
                    key={p}
                    className="flex items-center gap-2 text-[11px] font-mono cursor-pointer hover:bg-muted/50 px-1.5 py-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p)}
                      onChange={() => togglePermission(p)}
                    />
                    {p}
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs"
            onClick={submit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
            ) : null}
            Create role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Impersonation Audit
// ═══════════════════════════════════════════════════════════════════════════

function ImpersonationSection() {
  const [sessions, setSessions] = useState<ImpersonationSessionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState<ImpersonationSessionRow | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{
        sessions: ImpersonationSessionRow[];
        total: number;
      }>('/admin/security/privileged/impersonation?pageSize=100');
      setSessions(data.sessions);
      setTotal(data.total);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function endSession(s: ImpersonationSessionRow) {
    try {
      await apiClient.post(
        `/admin/security/privileged/impersonation/${s.id}/end`,
        {}
      );
      toast.success('Impersonation session ended');
      setEnding(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'End failed';
      toast.error(msg);
    }
  }

  return (
    <SectionCard
      title="Impersonation Audit"
      icon={UserCheck}
      description={`${total} impersonation session${total === 1 ? '' : 's'} in this tenant. Force-end any active session immediately.`}
    >
      <Card>
        <CardContent className="p-0">
          <div className="max-h-[400px] overflow-y-auto">
            <table
              className="w-full"
              style={{ tableLayout: 'fixed' }}
            >
              <colgroup>
                <col style={{ width: '14%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead>
                <tr className="border-b bg-muted/30 sticky top-0">
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Started
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Impersonator
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Target
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Reason
                  </th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-8 text-[11px] text-muted-foreground"
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />
                      Loading sessions…
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-8 text-[11px] text-muted-foreground italic"
                    >
                      No impersonation sessions recorded yet.
                    </td>
                  </tr>
                ) : (
                  sessions.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-3 py-2 text-[11px]">
                        <span title={new Date(s.startedAt).toISOString()}>
                          {relativeTime(s.startedAt)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[11px] truncate font-mono">
                        {s.impersonatorEmail ?? s.impersonatorId.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2 text-[11px] truncate font-mono">
                        {s.impersonatedEmail ?? s.impersonatedUserId.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2 text-[11px]">
                        <ImpersonationStatusBadge status={s.status} />
                      </td>
                      <td className="px-3 py-2 text-[11px] truncate" title={s.reason}>
                        {s.reason}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {s.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] gap-1"
                            onClick={() => setEnding(s)}
                            title="Force-end this active impersonation session"
                          >
                            <RotateCcw className="h-3 w-3" />
                            End
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {ending && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setEnding(null)}
          title="Force-end impersonation session"
          description={`End the active session where ${ending.impersonatorEmail ?? 'admin'} is impersonating ${ending.impersonatedEmail ?? 'user'}. The session will be revoked immediately.`}
          consequences={[
            'Impersonation session is revoked immediately',
            'Audit chain entry recorded with high-risk action tag',
            'Impersonator must log in again to start a new session',
          ]}
          confirmPhrase="end session"
          confirmLabel="End session"
          variant="danger"
          onConfirm={() => endSession(ending)}
        />
      )}
    </SectionCard>
  );
}

function ImpersonationStatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <Badge className="text-[10px] h-5 px-1.5 bg-emerald-500 text-white">
        Active
      </Badge>
    );
  }
  if (status === 'revoked') {
    return (
      <Badge className="text-[10px] h-5 px-1.5 bg-red-500 text-white">
        Revoked
      </Badge>
    );
  }
  if (status === 'expired') {
    return (
      <Badge variant="outline" className="text-[10px] h-5 px-1.5">
        Expired
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] h-5 px-1.5">
      {status}
    </Badge>
  );
}
